import { TrackingCommand } from './TrackingInterface'
import { eventQueue } from './EventQueue'
import { isMobile } from '@/utils/app/detect'

/**
 * BaseTrackingCommand centralizes logic for pushing events to the dataLayer or eventQueue.
 */
export abstract class BaseTrackingCommand implements TrackingCommand {
  /**
   * Pushes an event to the dataLayer or enqueues it for later processing.
   * @param eventData - The event data to push or enqueue.
   */
  protected pushToDataLayer(eventData: Record<string, any>): void {
    eventQueue.enqueue(eventData)
  }

  /**
   * Executes the command. Must be implemented by subclasses.
   * @param data - The data required to execute the command.
   */
  abstract execute(data: any): void
}

export class PageSetupCommand extends BaseTrackingCommand {
  execute(data: Record<string, any>): void {
    const pageSetupLayer = {
      env_template: data.envTemplate || '',
      env_device: isMobile(data?.device) ? 'mobile' : 'desktop',
      env_bugtracking_whitelist_domains: '',
      env_site: 'www.wetter.de',
      env_work: 'production',
      page_context: data.pageContext || '',
      page_path: data.pagePath || '',
      user_country: 'DE',
      page_ivw_level_1: data.ivwLvl1 || 'wetterde',
      page_ivw_level_2: data.ivwLvl2 || 'default',
      page_ivw_level_3: data.ivwLvl3 || '',
      page_ivw_level_4: data.ivwLvl4 || '',
      page_ivw_level_5: data.ivwLvl5 || '',
      page_ivw_level_6: data.ivwLvl6 || '',
      page_id: data.pageId || '47823',
      search_keywords: '',
      article_asset_types: '',
      article_headline: '',
      article_master_id: '',
      article_master_categories: '',
      article_home_site: '',
      article_home_page_context: '',
      article_date_published_first: '',
      article_date_published: '',
      article_first_published_datetime: '',
      article_last_published_datetime: '',
      article_ressort: '',
      agof_code: data.agofCode || 'dbrsowf_tak_default',
      agof_offer: data.agofOffer || 'wettede',
      cam_id: '',
      cam_provider: '',
      area_id: data.area_id || '',
      area_type: data.area_type || '',
      area_name: data.area_name || '',
      area_province_id: data.area_province_id || '',
      area_country_id: data.area_country_id || '',
      area_continent_id: data.area_continent_id || '',
      forecast_day: '',
      forecast_days: '',
      map_type: '',
      oewa_seitenkontigent: data.oewa || 'RedCont/Wetter/Wetterueberblick',
      rain_radar_home_enabled: '',
      ...data,
    }
    this.pushToDataLayer(pageSetupLayer)
    this.pushToDataLayer({
      event: 'Pageview',
      pageUrl: document?.baseURI,
      pageTitle: document?.title,
    })
  }
}

export class TrackEventCommand extends BaseTrackingCommand {
  execute(data: Record<string, any>): void {
    const eventData = {
      eventname: 'ga4_event',
      ...data,
    }
    this.pushToDataLayer(eventData)
  }
}

export class LocationSearchOpenedEventCommand extends BaseTrackingCommand {
  execute(data?: Record<string, any>): void {
    const eventData = {
      eventname: 'location_search_opened',
      ...data,
    }
    this.pushToDataLayer(eventData)
  }
}

export class LocationSearchSubmittedFreeEventCommand extends BaseTrackingCommand {
  execute(data: string): void {
    const eventData = {
      eventname: 'location_search_submitted',
      location_search_type: 'free',
      location_search_term: data,
    }
    this.pushToDataLayer(eventData)
  }
}

export class LocationSearchSubmittedSuggestionEventCommand extends BaseTrackingCommand {
  execute(data: string): void {
    const eventData = {
      eventname: 'location_search_submitted',
      navigation_type: 'suggestion',
      location_search_term: data,
    }
    this.pushToDataLayer(eventData)
  }
}

export class NavigationClickedEventCommand extends BaseTrackingCommand {
  execute(data: Record<string, any>): void {
    const eventData = {
      eventname: 'navigation_clicked',
      ...data,
    }
    this.pushToDataLayer(eventData)
  }
}

export class TeaserClickedEventCommand extends BaseTrackingCommand {
  execute(data: Record<string, any>): void {
    const eventData = {
      eventname: 'teaser_clicked',
      ...data,
    }
    this.pushToDataLayer(eventData)
  }
}

export class TeaserViewedEventCommand extends BaseTrackingCommand {
  execute(data: Record<string, any>): void {
    const eventData = {
      eventname: 'teaser_viewed',
      ...data,
    }
    this.pushToDataLayer(eventData)
  }
}
